import { useState, useEffect, useCallback } from "react";
import { Loader2, Mail, MailOpen, Check, Reply } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { api } from "../lib/api";

export const AdminContactMessages = () => {
  const [messages, setMessages] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/contact-messages");
      setMessages(data);
    } catch (e) {
      setMessages([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const markRead = async (id) => {
    try {
      await api.post(`/admin/contact-messages/${id}/read`);
      setMessages((m) => m.map((x) => (x.id === id ? { ...x, read: true } : x)));
    } catch (e) {
      toast.error("Impossible de marquer comme lu.");
    }
  };

  const toggle = (msg) => {
    const next = openId === msg.id ? null : msg.id;
    setOpenId(next);
    if (next && !msg.read) markRead(msg.id);
  };

  if (!messages) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-500">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="glass rounded-2xl p-6" data-testid="admin-contact-messages">
      <h3 className="font-heading text-lg font-semibold mb-4">Demandes de contact</h3>
      {messages.length === 0 ? (
        <p className="text-sm text-slate-500 py-8 text-center">Aucune demande reçue pour le moment.</p>
      ) : (
        <div className="space-y-2">
          {messages.map((m) => (
            <div
              key={m.id}
              data-testid={`contact-message-${m.id}`}
              className={`rounded-xl border px-4 py-3 transition-colors ${
                m.read ? "bg-slate-50 border-slate-200" : "bg-emerald-50/60 border-emerald-300"
              }`}
            >
              <button
                type="button"
                onClick={() => toggle(m)}
                className="w-full flex items-center justify-between gap-3 text-left"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {m.read ? (
                      <MailOpen className="w-4 h-4 text-slate-400 shrink-0" />
                    ) : (
                      <Mail className="w-4 h-4 text-emerald-600 shrink-0" />
                    )}
                    <span className="text-sm font-medium text-slate-900 truncate">{m.name}</span>
                    {!m.read && (
                      <Badge variant="outline" className="bg-emerald-500/15 text-emerald-700 border-emerald-500/30 text-[10px]">
                        Nouveau
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 truncate mt-0.5">
                    {m.subject} · {m.email}
                  </div>
                </div>
                <div className="text-xs text-slate-400 shrink-0 whitespace-nowrap">
                  {m.created_at ? new Date(m.created_at).toLocaleString("fr-FR") : ""}
                </div>
              </button>

              {openId === m.id && (
                <div className="mt-3 pt-3 border-t border-slate-200 space-y-2" data-testid={`contact-detail-${m.id}`}>
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs text-slate-600">
                    {m.company && <div>Société : <b className="text-slate-800">{m.company}</b></div>}
                    {m.phone && <div>Téléphone : <b className="text-slate-800">{m.phone}</b></div>}
                    <div>E-mail : <b className="text-slate-800">{m.email}</b></div>
                    <div>Sujet : <b className="text-slate-800">{m.subject}</b></div>
                  </div>
                  <p className="text-sm text-slate-800 whitespace-pre-wrap bg-white border border-slate-200 rounded-lg p-3">
                    {m.message}
                  </p>
                  <div className="flex gap-2 pt-1">
                    <a href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject)}`}>
                      <Button size="sm" variant="outline" className="border-slate-200 bg-slate-100 hover:bg-slate-200">
                        <Reply className="w-3.5 h-3.5 mr-1.5" /> Répondre
                      </Button>
                    </a>
                    {!m.read && (
                      <Button
                        size="sm"
                        data-testid={`mark-read-${m.id}`}
                        onClick={() => markRead(m.id)}
                        className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950"
                      >
                        <Check className="w-3.5 h-3.5 mr-1.5" /> Marquer comme lu
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
